import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DashboardWidget, WidgetSize } from '@sea-snag/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@sea-snag/ui';
import { GripVertical, Settings, X } from 'lucide-react';
import React, { Suspense } from 'react';

import { removeWidget } from '../../stores/dashboard-store';
import { WidgetConfigPopover } from './widget-config-popover';
import { WIDGET_REGISTRY, type WidgetRegistration } from './widget-registry';

// ── Size to grid class mapping ───────────────────────────────────────

const SIZE_CLASSES: Record<WidgetSize, string> = {
  third: 'col-span-12 md:col-span-6 lg:col-span-4',
  half: 'col-span-12 lg:col-span-6',
  full: 'col-span-12',
};

// ── Loading Skeleton ─────────────────────────────────────────────────

function WidgetSkeleton() {
  return (
    <div className="space-y-3 p-1">
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

// ── Widget Wrapper ───────────────────────────────────────────────────

interface WidgetWrapperProps {
  widget: DashboardWidget;
  isEditMode: boolean;
}

export function WidgetWrapper({ widget, isEditMode }: WidgetWrapperProps) {
  const registration: WidgetRegistration | undefined = WIDGET_REGISTRY[widget.type];

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: widget.id,
    disabled: !isEditMode,
  });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  if (!registration) return null;

  const Icon = registration.icon;
  const WidgetComponent = registration.component;
  const hasConfig = registration.configFields.length > 0;

  return (
    <div ref={setNodeRef} style={style} className={SIZE_CLASSES[registration.size]}>
      <Card className={`relative h-full ${isDragging ? 'invisible' : ''}`}>
        {isEditMode && (
          <Button
            variant="destructive"
            size="icon-xs"
            onClick={() => removeWidget(widget.id)}
            className="absolute top-[4px] right-[4px] z-10 size-7 rounded-full shadow-sm"
          >
            <X className="size-3" />
          </Button>
        )}
        <CardHeader className="flex flex-row items-center gap-2">
          {isEditMode && (
            <button
              className="text-muted-foreground hover:text-foreground -ml-1 cursor-grab touch-none active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="size-4" />
            </button>
          )}
          <Icon className="text-muted-foreground size-4 shrink-0" />
          <CardTitle className="flex-1 truncate text-sm">{registration.name}</CardTitle>
          {hasConfig && !isEditMode && (
            <WidgetConfigPopover widget={widget} registration={registration}>
              <Button variant="ghost" size="icon-xs">
                <Settings className="size-3.5" />
              </Button>
            </WidgetConfigPopover>
          )}
        </CardHeader>
        <CardContent>
          <Suspense fallback={<WidgetSkeleton />}>
            <WidgetComponent widgetId={widget.id} config={widget.config} />
          </Suspense>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Drag Overlay Preview ────────────────────────────────────────────

interface WidgetPreviewProps {
  widget: DashboardWidget;
}

export function WidgetPreview({ widget }: WidgetPreviewProps) {
  const registration = WIDGET_REGISTRY[widget.type];
  if (!registration) return null;

  const Icon = registration.icon;

  return (
    <Card className="border-primary w-64 rotate-2 shadow-xl">
      <CardHeader className="flex flex-row items-center gap-2">
        <GripVertical className="text-muted-foreground size-4" />
        <Icon className="text-muted-foreground size-4 shrink-0" />
        <CardTitle className="flex-1 truncate text-sm">{registration.name}</CardTitle>
      </CardHeader>
    </Card>
  );
}
